import React, { useMemo, useState } from 'react';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ScheduleDesviationDialog } from 'components/dialog/schedule_desviation/ScheduleDesviationDialog';
import { useLocation, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import CachedIcon from '@mui/icons-material/Cached';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { buildSettingsBasePath, buildSettingsCrudPath, parseSettingsCrudRoute } from 'utils/settingsCrudRouting';

interface SubMenuProps {
    contextMenu: any;
    setContextMenu: (value: any) => void;
    selectedRow: any;
    reloading: () => void;
    rows: any;
    cleanSelected: () => void;
}

export const ContextMenu = ({ selectedRow, cleanSelected, rows, contextMenu, setContextMenu, reloading }: SubMenuProps) => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const [localOpen, setLocalOpen] = useState(false);
    const [localMode, setLocalMode] = useState<'add' | 'edit' | 'delete' | 'info' | 'duplicate'>('add');
    const basePath = buildSettingsBasePath('schedule_deviation');
    const modalRoute = useMemo(() => parseSettingsCrudRoute(location.pathname, 'schedule_deviation'), [location.pathname]);
    const routeOpen = modalRoute.action === 'add' || (modalRoute.action === 'update' && modalRoute.id !== null) || (modalRoute.action === 'detail' && modalRoute.id !== null);
    const routeMode: 'add' | 'edit' | 'info' = modalRoute.action === 'add' ? 'add' : modalRoute.action === 'detail' ? 'info' : 'edit';
    const open = routeOpen || localOpen;
    const mode = routeOpen ? routeMode : localMode;

    const selectedRowFromRoute = useMemo(() => {
        if (modalRoute.id === null) return null;
        return rows.find((row: any) => Number(row.id) === modalRoute.id) || null;
    }, [rows, modalRoute.id]);
    const selectedRowsForDialog = selectedRowFromRoute ? [selectedRowFromRoute] : selectedRow;

    const closeDialog = () => {
        if (routeOpen) {
            navigate(basePath);
        } else {
            setLocalOpen(false);
        }
        cleanSelected();
    };

    const options = [
        {
            name: t('settings.schedule_desviation.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildSettingsCrudPath('schedule_deviation', 'add'));
                setContextMenu(null);
            },
            color: '',
            selected: false,
            onlyOne: false,
            permission: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.CreateScheduleDesviationRule') ? true : false,
        },
        {
            name: t('settings.schedule_desviation.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('schedule_deviation', 'update', current.id));
                setContextMenu(null);
            },
            color: '',
            disabled: selectedRow.length !== 1 ? true : false,
            permission: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.UpdateScheduleDesviationRule') ? true : false,
        },
        {
            name: t('settings.schedule_desviation.table.context_menu.duplicate'),
            icon: <ContentCopyIcon />,
            click: () => {
                setLocalMode('duplicate');
                setLocalOpen(true);
                setContextMenu(null);
            },
            color: '',
            disabled: selectedRow.length !== 1 ? true : false,
            permission: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.DuplicateScheduleDesviationRule') ? true : false,
        },
        {
            name: t('settings.schedule_desviation.table.context_menu.delete'),
            icon: <DeleteIcon color="error" />,
            click: () => {
                setLocalMode('delete');
                setLocalOpen(true);
                setContextMenu(null);
            },
            color: 'error',
            disabled: true, //selectedRow.length <= 0 ? true : false,
            selected: false,
            onlyOne: true,
            permission: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.DeleteScheduleDesviationRule') ? true : false,
        },
        {
            name: t('settings.schedule_desviation.table.context_menu.info'),
            icon: <InfoIcon />,
            disabled: selectedRow.length !== 1 ? true : false,
            click: () => {
                const current = selectedRow[0];
                if (!current?.id) return;
                navigate(buildSettingsCrudPath('schedule_deviation', 'detail', current.id));
                setContextMenu(null);
            },
            color: '',
            permission: JSON.parse(localStorage.getItem('permissions') || '[]').find((permision: string) => permision === 'timekeeper.ScheduleDesviationRuleById') ? true : false,
        },
        {
            name: t('settings.schedule_desviation.table.context_menu.reload'),
            icon: <CachedIcon />,
            color: '',
            click: () => {
                reloading();
                setContextMenu(null);
            },
            permission: true,
        },
    ];

    return (
        <>
            <Menu
                open={contextMenu ? true : false}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options
                    .filter((option) => option.permission)
                    .map((option, index) => (
                        <MenuItem key={index} onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>

                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    ))}
            </Menu>

            <ScheduleDesviationDialog open={open} onClose={closeDialog} reloading={reloading} rows={rows} selectedRow={selectedRowsForDialog} mode={mode} />
        </>
    );
};
